import { useState, useMemo, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Minus, Trash2, ArrowLeft, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function Cart() {
  const navigate = useNavigate();
  const { cart, updateQuantity, clearCart, currentRoom, setLastTransaction } = useOutletContext();
  const [confirmModal, setConfirmModal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [gratPercent, setGratPercent] = useState(0);

  useEffect(() => {
    api.get("/pos/settings").then(res => {
      setGratPercent(res.data.auto_gratuity_percent || 0);
    }).catch(() => {});
  }, []);

  const taxBreakdown = useMemo(() => {
    const taxMap = {};
    let subtotal = 0;
    let totalTax = 0;

    cart.forEach(item => {
      const lineTotal = item.unit_price * item.quantity;
      subtotal += lineTotal;
      (item.tax_rates || []).forEach(rate => {
        const r = rate.rate_percent / 100;
        let taxPerUnit;
        if (rate.is_inclusive) {
          taxPerUnit = item.unit_price - (item.unit_price / (1 + r));
        } else {
          taxPerUnit = item.unit_price * r;
        }
        const taxAmount = Math.round(taxPerUnit * item.quantity * 100) / 100;
        totalTax += taxAmount;
        const key = rate.tax_name;
        taxMap[key] = taxMap[key] || { name: rate.tax_name, rate: rate.rate_percent, inclusive: rate.is_inclusive, amount: 0 };
        taxMap[key].amount += taxAmount;
      });
    });

    const inclusiveTax = Object.values(taxMap).filter(t => t.inclusive).reduce((s, t) => s + t.amount, 0);
    // Service charge always applied — not removable by cashier
    const gratuity = gratPercent > 0 ? Math.round(subtotal * gratPercent) / 100 : 0;
    const grandTotal = Math.round((subtotal + totalTax - inclusiveTax + gratuity) * 100) / 100;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxes: Object.values(taxMap).map(t => ({ ...t, amount: Math.round(t.amount * 100) / 100 })),
      totalTax: Math.round(totalTax * 100) / 100,
      gratuity: Math.round(gratuity * 100) / 100,
      grandTotal
    };
  }, [cart, gratPercent]);

  const postCharge = async () => {
    if (!currentRoom) { toast.error("No room selected"); return; }
    setPosting(true);
    try {
      const res = await api.post("/pos/charge", {
        room_number: currentRoom.room_number,
        reservation_id: currentRoom.reservation_id || '',
        guest_name: currentRoom.guest_name || '',
        items: cart.map(item => ({ item_id: item.id, quantity: item.quantity })),
        gratuity_cents: Math.round(taxBreakdown.gratuity * 100),
        gratuity_removed: false,
      });
      setLastTransaction(res.data);
      clearCart();
      setConfirmModal(false);
      navigate("/pos/receipt");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Charge failed");
    } finally { setPosting(false); }
  };

  if (cart.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center" data-testid="empty-cart">
        <ShoppingCart className="w-12 h-12 text-gray-200 mb-4" />
        <p className="text-gray-400 mb-4">Cart is empty</p>
        <Button onClick={() => navigate("/pos/menu")} className="rounded-none bg-black text-white" data-testid="browse-menu-button">
          Browse Menu
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="cart-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
        <button onClick={() => navigate("/pos/menu")} className="text-gray-400 hover:text-black" data-testid="back-to-menu">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="font-heading text-lg font-bold tracking-tight">Cart</h2>
        <span className="text-xs text-gray-400 ml-auto">Room {currentRoom?.room_number}</span>
      </div>

      {/* Items */}
      <div className="flex-1 overflow-auto">
        <div className="divide-y divide-gray-100">
          {cart.map((item) => (
            <div key={item.id} className="px-4 py-3 flex items-center gap-3" data-testid={`cart-item-${item.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{item.item_name}</p>
                <p className="text-xs text-gray-400">${item.unit_price.toFixed(2)} each</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => updateQuantity(item.id, item.quantity - 1)}
                  className="w-8 h-8 flex items-center justify-center border border-gray-200 bg-gray-50 active:bg-black active:text-white"
                  data-testid={`cart-decrease-${item.id}`}>
                  {item.quantity === 1 ? <Trash2 className="w-3 h-3 text-red-400" /> : <Minus className="w-3 h-3" />}
                </button>
                <span className="w-8 text-center font-heading font-bold">{item.quantity}</span>
                <button onClick={() => updateQuantity(item.id, item.quantity + 1)}
                  className="w-8 h-8 flex items-center justify-center border border-gray-200 bg-gray-50 active:bg-black active:text-white"
                  data-testid={`cart-increase-${item.id}`}>
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <p className="font-heading font-bold text-sm w-16 text-right">
                ${(item.unit_price * item.quantity).toFixed(2)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Tax Breakdown & Service Charge & Total */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Subtotal</span>
          <span className="font-mono">${taxBreakdown.subtotal.toFixed(2)}</span>
        </div>
        {taxBreakdown.taxes.map((tax) => (
          <div key={tax.name} className="flex justify-between text-xs text-gray-400">
            <span>{tax.name} ({tax.rate}%){tax.inclusive ? " incl." : ""}</span>
            <span className="font-mono">${tax.amount.toFixed(2)}</span>
          </div>
        ))}

        {/* Service Charge — always applied, not removable */}
        {gratPercent > 0 && (
          <div className="flex justify-between text-sm pt-1 border-t border-gray-200" data-testid="gratuity-row">
            <span className="text-gray-700 font-medium">Service Charge ({gratPercent}%)</span>
            <span className="font-mono">${taxBreakdown.gratuity.toFixed(2)}</span>
          </div>
        )}

        <div className="flex justify-between pt-2 border-t border-gray-200">
          <span className="font-heading font-bold">Total</span>
          <span className="font-heading text-2xl font-black" data-testid="cart-grand-total">
            ${taxBreakdown.grandTotal.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Charge Button */}
      <div className="p-4 bg-white border-t border-gray-200">
        <Button onClick={() => setConfirmModal(true)}
          className="w-full h-14 rounded-none bg-black text-white hover:bg-gray-800 font-bold text-base"
          data-testid="charge-room-button">
          Post to Room {currentRoom?.room_number}
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmModal} onOpenChange={setConfirmModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirm Charge</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>Charge <strong>${taxBreakdown.grandTotal.toFixed(2)}</strong> to:</p>
            <p className="font-heading text-lg font-bold">Room {currentRoom?.room_number}</p>
            {taxBreakdown.gratuity > 0 && (
              <p className="text-xs text-gray-500">Includes ${taxBreakdown.gratuity.toFixed(2)} service charge ({gratPercent}%)</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmModal(false)} className="rounded-none flex-1">Cancel</Button>
            <Button onClick={postCharge} disabled={posting}
              className="rounded-none bg-black text-white hover:bg-gray-800 flex-1"
              data-testid="confirm-charge-button">
              {posting ? "Posting..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
